<!-- UI-5 (5f): compact, self-contained recent-runs list for the Pipelines flyout.
     Replaces the wide Buefy b-table that overflowed the panel. Each run is a full-width
     row (status dot + task name + time); click to expand its inputs/outputs (fetched on
     demand) in a localized scroll area so the panel itself never scrolls horizontally. -->
<template>
  <ul class="rr-list">
    <li v-for="run in taskRuns" :key="run.id" class="rr-li">
      <div class="rr-head">
        <button class="rr-row" @click="toggle(run)">
          <task-run-status-dot :state="run.state" />
          <span class="rr-name">{{ pipelineDisplayName(run.task) }}</span>
          <span class="rr-time">{{ fmt(run.createdAt) }}</span>
          <i class="fas rr-chev" :class="expandedId === run.id ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
        </button>
        <button v-if="canDelete" class="rr-del" :title="$t('delete')" @click.stop="$emit('delete', run)">
          <i class="fas fa-trash"></i>
        </button>
      </div>
      <div v-show="expandedId === run.id" class="rr-detail">
        <template v-if="run.inputs && run.inputs.length">
          <div class="rr-d-label">{{ $t('app-engine.inputs.title') }}</div>
          <task-run-parameters-table :parameters="run.inputs" :project-id="run.project" type="input" />
        </template>
        <template v-if="run.outputs && run.outputs.length">
          <div class="rr-d-label">{{ $t('app-engine.outputs.title') }}</div>
          <task-run-parameters-table :parameters="run.outputs" :project-id="run.project" type="output" />
        </template>
        <div v-if="!hasIo(run)" class="rr-empty"><em>—</em></div>
      </div>
    </li>
  </ul>
</template>

<script>
import TaskRunStatusDot from '@/components/appengine/task-run/TaskRunStatusDot';
import TaskRunParametersTable from '@/components/appengine/task-run/TaskRunParametersTable';
import {pipelineDisplayName} from '@/utils/appengine/pipeline-display';

export default {
  name: 'recent-runs-list',
  components: {TaskRunStatusDot, TaskRunParametersTable},
  props: {
    taskRuns: {type: Array, required: true},
    canDelete: {type: Boolean, default: false},
  },
  data() {
    return {expandedId: null};
  },
  methods: {
    pipelineDisplayName,
    hasIo(run) {
      return (run.inputs && run.inputs.length) || (run.outputs && run.outputs.length);
    },
    async toggle(run) {
      if (this.expandedId === run.id) {
        this.expandedId = null;
        return;
      }
      this.expandedId = run.id;
      try {
        if (!run.inputs) {
          await run.fetchInputs();
        }
        if (!run.outputs) {
          await run.fetchOutputs();
        }
      } catch (e) {
        console.log(e);
      }
    },
    fmt(d) {
      if (!d) {
        return '';
      }
      let date = new Date(d);
      let now = new Date();
      let sameDay = date.toDateString() === now.toDateString();
      return sameDay
        ? date.toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'})
        : date.toLocaleDateString([], {month: 'short', day: 'numeric'});
    }
  }
};
</script>

<style scoped>
.rr-list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.rr-li {
  border-bottom: 1px solid #eef0f4;
}

.rr-li:last-child {
  border-bottom: 0;
}

.rr-head {
  display: flex;
  align-items: center;
}

.rr-row {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  background: none;
  border: 0;
  padding: 8px 2px;
  cursor: pointer;
  text-align: left;
}

.rr-row:hover {
  background: #fafbfc;
}

.rr-del {
  flex: 0 0 auto;
  background: none;
  border: 0;
  padding: 6px 8px;
  color: #c4cad6;
  cursor: pointer;
  border-radius: 6px;
}

.rr-del:hover {
  color: #e0245e;
  background: #fbeaef;
}

.rr-name {
  flex: 1;
  min-width: 0;
  font-weight: 600;
  color: #0e1116;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.rr-time {
  font-family: 'JetBrains Mono', ui-monospace, monospace;
  font-size: 10px;
  color: #8a91a1;
  flex: 0 0 auto;
}

.rr-chev {
  font-size: 10px;
  color: #c4cad6;
  flex: 0 0 auto;
}

/* localized scroll: only the detail can scroll, never the whole panel */
.rr-detail {
  max-width: 100%;
  overflow-x: auto;
  padding: 4px 2px 10px;
}

.rr-d-label {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #8a91a1;
  margin: 4px 0;
}

.rr-empty {
  color: #8a91a1;
  font-size: 11px;
  padding: 4px 0;
}
</style>
